import React from "react";
import { ImageIcon } from "lucide-react";
import { SmartLink } from "../smart-link";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { imagePositionStyle, type ImagePosition } from "../../lib/image-position";
import { plainText } from "../../lib/plain-text";
import type { BaseComponentProps } from "../../types/puck";
import {
  ComparisonFrame,
  type ComparisonAspectRatio,
  type ComparisonStartPosition,
} from "./comparison-frame";

export interface BeforeAfterPair {
  beforeImageUrl?: string;
  afterImageUrl?: string;
  caption?: string;
  alt?: string;
  beforeFocus?: ImagePosition;
  afterFocus?: ImagePosition;
}

export interface BeforeAfterProps extends BaseComponentProps {
  heading?: string;
  subheading?: string;
  beforeLabel?: string;
  afterLabel?: string;
  aspectRatio?: ComparisonAspectRatio;
  startPosition?: ComparisonStartPosition;
  ctaText?: string;
  ctaHref?: string;
  backgroundColor?: string;
  pairs?: BeforeAfterPair[];
  isEditing?: boolean;
}

/** A pair renders on live sites once it has at least one photo. */
function hasAnyImage(pair: BeforeAfterPair): boolean {
  return !!(pair?.beforeImageUrl || pair?.afterImageUrl);
}

function pairAlt(pair: BeforeAfterPair, heading: string | undefined, side: string): string {
  const subject = pair.alt || pair.caption || plainText(heading || "") || "the work";
  return `${side}: ${subject}`;
}

/**
 * One featured comparison at a time. With several pairs, a thumbnail strip
 * below swaps which pair is featured; a grid of simultaneous live sliders is
 * deliberately not offered.
 */
export const BeforeAfter: React.FC<BeforeAfterProps> = ({
  heading,
  subheading,
  beforeLabel,
  afterLabel,
  aspectRatio = "4:3",
  startPosition = "half",
  ctaText,
  ctaHref,
  backgroundColor,
  pairs: pairsProp,
  isEditing = false,
  className,
}) => {
  const allPairs = Array.isArray(pairsProp) ? pairsProp : [];
  // Editors see every pair (including empty ones being built); visitors only
  // see pairs with a photo.
  const pairs = isEditing ? allPairs : allPairs.filter(hasAnyImage);
  const [activeIndex, setActiveIndex] = React.useState(0);
  // Clamp once and use everywhere: after deleting pairs in the editor, the
  // raw state can point past the end, and the thumbnail ring must follow the
  // same pair the featured frame shows.
  const clampedIndex = Math.min(activeIndex, Math.max(0, pairs.length - 1));
  const active = pairs[clampedIndex];

  if (pairs.length === 0 && !isEditing) {
    return null;
  }

  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;
  const showsCta = !!ctaText && !!ctaHref;
  const groupLabel = `Compare ${beforeLabel || "before"} and ${afterLabel || "after"}${
    active?.caption ? `: ${active.caption}` : ""
  }`;

  return (
    <section
      className={cn(
        "jobsite:py-16 jobsite:md:py-24",
        backgroundColor ? "" : "jobsite:bg-background",
        className,
      )}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        {heading || subheading ? (
          <div className="jobsite:mx-auto jobsite:mb-12 jobsite:max-w-3xl jobsite:text-center">
            {heading ? (
              <h2 className="jobsite:font-heading jobsite:text-3xl jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-4xl jobsite:md:text-5xl">
                {heading}
              </h2>
            ) : null}
            {subheading ? (
              <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground jobsite:md:text-xl">
                {subheading}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="jobsite:mx-auto jobsite:max-w-4xl">
          {active ? (
            <div
              className="jobsite:overflow-hidden jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:shadow-sm"
              style={surfaceRadiusStyle}
            >
              {active.beforeImageUrl && active.afterImageUrl ? (
                <ComparisonFrame
                  key={`${activeIndex}-${active.beforeImageUrl}-${active.afterImageUrl}`}
                  beforeSrc={active.beforeImageUrl}
                  afterSrc={active.afterImageUrl}
                  beforeAlt={pairAlt(active, heading, beforeLabel || "Before")}
                  afterAlt={pairAlt(active, heading, afterLabel || "After")}
                  beforeLabel={beforeLabel}
                  afterLabel={afterLabel}
                  beforeFocus={active.beforeFocus}
                  afterFocus={active.afterFocus}
                  aspectRatio={aspectRatio}
                  startPosition={startPosition}
                  eager={clampedIndex === 0}
                  groupLabel={groupLabel}
                />
              ) : (
                <PartialPair
                  pair={active}
                  heading={heading}
                  beforeLabel={beforeLabel}
                  afterLabel={afterLabel}
                  aspectRatio={aspectRatio}
                  isEditing={isEditing}
                />
              )}
              {active.caption ? (
                <div className="jobsite:px-4 jobsite:py-4 jobsite:sm:px-5">
                  <div className="jobsite:font-heading jobsite:text-xl jobsite:text-foreground">
                    {active.caption}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {pairs.length > 1 ? (
            <div className="jobsite:mt-4 jobsite:flex jobsite:flex-wrap jobsite:justify-center jobsite:gap-2">
              {pairs.map((pair, index) => {
                const thumbnailUrl = pair.afterImageUrl || pair.beforeImageUrl;
                const isActive = index === clampedIndex;
                return (
                  <button
                    key={`${thumbnailUrl || "empty"}-${index}`}
                    type="button"
                    aria-label={pair.caption || `Comparison ${index + 1} of ${pairs.length}`}
                    aria-current={isActive}
                    onClick={() => setActiveIndex(index)}
                    className={cn(
                      "jobsite:h-14 jobsite:w-20 jobsite:shrink-0 jobsite:overflow-hidden jobsite:rounded-md jobsite:bg-muted jobsite:transition-opacity",
                      isActive
                        ? "jobsite:opacity-100 jobsite:ring-2 jobsite:ring-primary"
                        : "jobsite:opacity-60 jobsite:hover:opacity-90",
                    )}
                  >
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt=""
                        loading="lazy"
                        style={imagePositionStyle(pair.afterFocus)}
                        className="jobsite:h-full jobsite:w-full jobsite:object-cover"
                      />
                    ) : (
                      <span className="jobsite:flex jobsite:h-full jobsite:w-full jobsite:items-center jobsite:justify-center jobsite:text-muted-foreground">
                        <ImageIcon className="jobsite:h-4 jobsite:w-4" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : null}

          {showsCta ? (
            <div className="jobsite:mt-8 jobsite:text-center">
              <Button asChild size="lg">
                <SmartLink to={ctaHref}>{ctaText}</SmartLink>
              </Button>
            </div>
          ) : ctaText && isEditing ? (
            <div className="jobsite:mt-8 jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground">
              Add a button link to show the "{ctaText}" button
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

/**
 * A pair without both photos: visitors get the one real photo as a plain
 * image (never a broken slider); editors get placeholders and the hint that
 * completes the comparison.
 */
function PartialPair({
  pair,
  heading,
  beforeLabel,
  afterLabel,
  aspectRatio,
  isEditing,
}: {
  pair: BeforeAfterPair;
  heading?: string;
  beforeLabel?: string;
  afterLabel?: string;
  aspectRatio: ComparisonAspectRatio;
  isEditing: boolean;
}) {
  const aspectClass = {
    "4:3": "jobsite:aspect-[4/3]",
    "16:9": "jobsite:aspect-[16/9]",
    "1:1": "jobsite:aspect-square",
    "3:4": "jobsite:aspect-[3/4]",
  }[aspectRatio];

  const presentUrl = pair.beforeImageUrl || pair.afterImageUrl;
  const presentSide = pair.beforeImageUrl ? beforeLabel || "Before" : afterLabel || "After";
  const missingSide = pair.beforeImageUrl ? "after" : "before";
  const presentFocus = pair.beforeImageUrl ? pair.beforeFocus : pair.afterFocus;

  if (!isEditing) {
    if (!presentUrl) {
      return null;
    }
    return (
      <div className={cn("jobsite:relative jobsite:overflow-hidden", aspectClass)}>
        <img
          src={presentUrl}
          alt={pairAlt(pair, heading, presentSide)}
          style={imagePositionStyle(presentFocus)}
          className="jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  if (!presentUrl) {
    return (
      <div className={cn("jobsite:grid jobsite:grid-cols-2", aspectClass)}>
        {[beforeLabel || "Before", afterLabel || "After"].map((side) => (
          <div
            key={side}
            className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:gap-2 jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/30 jobsite:text-muted-foreground"
          >
            <ImageIcon className="jobsite:h-6 jobsite:w-6" />
            <span className="jobsite:text-sm jobsite:font-medium">{side} photo</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("jobsite:relative jobsite:overflow-hidden", aspectClass)}>
      <img
        src={presentUrl}
        alt={pairAlt(pair, heading, presentSide)}
        style={imagePositionStyle(presentFocus)}
        className="jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover"
        loading="lazy"
      />
      <div className="jobsite:absolute jobsite:inset-x-0 jobsite:bottom-0 jobsite:bg-black/70 jobsite:px-4 jobsite:py-2 jobsite:text-center jobsite:text-sm jobsite:text-white">
        Add the {missingSide} photo to turn this into a comparison
      </div>
    </div>
  );
}
